import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  OpenInNew as OpenInNewIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { accountingAPI } from '../../../services/api';
import { formatCurrency } from '../../../utils/formatters';
import { StatStrip } from './ArShared';

// Bridges this AR ledger view back to the school's existing fee
// collection/billing flow: every invoice/receipt on this page that
// originated from a fee payment was pushed here by
// App\Services\Accounting\SchoolAccounting (billGroup/recordPayment), via
// the same GET /accounting/dashboard + sync-queue endpoints the standalone
// Accounting Dashboard (ledger-dashboard) already uses. Shown only on the
// Customer Ledger tab so the two systems are visibly reconciled where
// accountants look up a student's balance - fails silently (renders
// nothing) if the caller lacks the legacy ACCOUNTING.ACCOUNTS permission
// that gates it, since it's a supplementary widget, not the page itself.
const FeeSyncPanel = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [queue, setQueue] = useState(null);
  const [queueOpen, setQueueOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [retryPreview, setRetryPreview] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await accountingAPI.getDashboard();
      setData(response.data);
      setUnavailable(false);
    } catch {
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const loadQueue = async () => {
    try {
      const response = await accountingAPI.getSyncQueue();
      setQueue(response.data);
    } catch {
      // supplementary widget - leave queue empty on failure
    }
  };

  const retryQueue = async () => {
    setRetrying(true);
    setRetryResult(null);
    setRetryPreview(null);
    try {
      const response = await accountingAPI.retrySyncQueue();
      setRetryResult({ ok: true, partial: Number(response.data.failed_count || 0) > 0, message: response.data.message });
      fetchDashboard();
      if (queueOpen) loadQueue();
    } catch (err) {
      setRetryResult({ ok: false, message: err?.response?.data?.message || 'Retry failed.' });
    } finally {
      setRetrying(false);
    }
  };

  const previewQueue = async () => {
    setPreviewing(true);
    setRetryPreview(null);
    try {
      const response = await accountingAPI.previewSyncQueueRetry();
      setRetryPreview({ ok: true, data: response.data });
    } catch (err) {
      setRetryPreview({ ok: false, message: err?.response?.data?.message || 'Could not preview the retry queue.' });
    } finally {
      setPreviewing(false);
    }
  };

  if (unavailable) return null;

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Skeleton variant="text" width={220} />
          <Skeleton variant="rectangular" height={64} sx={{ mt: 1 }} />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const { kpis, ledger, sync_health: syncHealth } = data;
  const queueCount = (syncHealth.unbilled_allocations ?? 0) + (syncHealth.unpushed_payments ?? 0);
  const failedCount = (syncHealth.failed_allocations ?? 0) + (syncHealth.failed_payments ?? 0);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            <SyncIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
            Fee Collection Sync
          </Typography>
          <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate('/ledger/dashboard')}>
            Full accounting dashboard
          </Button>
        </Box>

        {!syncHealth.bridge_enabled ? (
          <Alert severity="info">Accounting bridge is off - fee billing/payments are not mirrored into this ledger.</Alert>
        ) : (
          <>
            <StatStrip
              tiles={[
                { label: 'Fees Billed', value: formatCurrency(kpis.billed) },
                { label: 'Fees Collected', value: formatCurrency(kpis.collected), color: 'success.main' },
                { label: 'Outstanding (fees)', value: formatCurrency(kpis.outstanding), color: 'warning.main' },
                {
                  label: 'Ledger Receivable',
                  value: ledger.receivable !== null ? formatCurrency(ledger.receivable) : 'Offline',
                  color: 'primary.main',
                },
              ]}
            />
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              {ledger.reachable ? (
                <Typography variant="caption" color="success.main" display="flex" alignItems="center" gap={0.5}>
                  <CheckCircleIcon fontSize="inherit" /> Ledger connected
                </Typography>
              ) : (
                <Typography variant="caption" color="error.main" display="flex" alignItems="center" gap={0.5}>
                  <ErrorIcon fontSize="inherit" /> Ledger offline
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                &middot; {queueCount === 0 ? 'Everything mirrored' : `${queueCount} fee record(s) not yet pushed`}
              </Typography>
              {failedCount > 0 && (
                <Typography variant="caption" color="error.main">&middot; {failedCount} sync failure(s)</Typography>
              )}
              {queueCount > 0 && (
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => { setQueueOpen((o) => !o); if (!queueOpen) loadQueue(); }}>
                    {queueOpen ? 'Hide' : 'View'} queue
                  </Button>
                  <Button size="small" disabled={previewing} onClick={previewQueue}>
                    {previewing ? 'Previewing...' : 'Preview retry'}
                  </Button>
                  <Button size="small" variant="contained" disabled={retrying} onClick={retryQueue}>
                    {retrying ? 'Retrying...' : 'Retry batch'}
                  </Button>
                </Stack>
              )}
            </Box>
            {retryPreview?.ok && (
              <Alert severity={retryPreview.data.can_retry ? 'info' : 'warning'} sx={{ mt: 1 }}>
                {retryPreview.data.can_retry
                  ? `Read-only preview: next batch will try up to ${retryPreview.data.next_batch.invoice_groups} invoice group(s) and ${retryPreview.data.next_batch.receipts} receipt(s). After one batch, about ${retryPreview.data.remaining_after_batch.invoice_groups} invoice group(s) and ${retryPreview.data.remaining_after_batch.receipts} receipt(s) will remain. ${retryPreview.data.ledger_checks}`
                  : `Retry is not ready. ${retryPreview.data.bridge_enabled ? '' : 'Accounting bridge is disabled. '}${retryPreview.data.missing_prerequisites?.length ? `Missing: ${retryPreview.data.missing_prerequisites.join(', ')}.` : ''}`}
              </Alert>
            )}
            {retryPreview && !retryPreview.ok && <Alert severity="error" sx={{ mt: 1 }}>{retryPreview.message}</Alert>}
            {retryResult && (
              <Alert severity={!retryResult.ok ? 'error' : (retryResult.partial ? 'warning' : 'success')} sx={{ mt: 1 }}>{retryResult.message}</Alert>
            )}
            <Collapse in={queueOpen && !!queue}>
              <Box mt={1}>
                {queue && [...queue.unbilled_allocations, ...queue.unpushed_payments].map((q) => (
                  <Typography key={`${q.type}-${q.id}`} variant="caption" display="block">
                    Source: {q.source_ref || 'not attempted'} &middot; Last attempt: {q.last_attempt_at || 'never'}<br />
                    {q.type === 'allocation' ? 'BILL' : 'RECEIPT'} &middot; {q.student} &middot; {q.detail}{q.term ? ` · ${q.term}` : ''} &middot; {formatCurrency(q.amount)} &middot; {q.date} &middot; attempts: {q.attempts}
                    {q.error ? ` · ${q.error}` : ''}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FeeSyncPanel;
